import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, BookOpen, Building2, CheckCircle2, Copy, CreditCard,
  Headphones, MapPin, ShoppingBag, Smartphone, Trash2, User,
} from "lucide-react";
import { useCart } from "../context/CartContext";
import { api } from "../api";
import { toast } from "sonner";

const FORMAT_ICONS = { ebook: BookOpen, audiobook: Headphones, aplikacja: Smartphone };

const FIELD_CLASS =
  "w-full rounded-xl border border-slate-700 bg-slate-900 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors focus:border-cyan-500";

export default function Checkout() {
  const { items, remove, total, checkout, checkingOut } = useCart();
  const navigate = useNavigate();
  const [bank, setBank] = useState(null);
  const [orderDone, setOrderDone] = useState(null);
  const [form, setForm] = useState({
    firstName: "", lastName: "", email: "", phone: "",
    street: "", postalCode: "", city: "",
    companyName: "", nip: "",
    method: "stripe",
  });

  useEffect(() => {
    api.get("/store/settings").then((r) => setBank(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!items.length && !orderDone) navigate("/");
  }, [items.length, orderDone, navigate]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.firstName.trim().length < 2 || form.lastName.trim().length < 2) {
      toast.error("Podaj imię i nazwisko."); return;
    }
    if (!form.email.includes("@")) {
      toast.error("Podaj poprawny adres e-mail."); return;
    }
    if (form.phone.trim().length < 6) {
      toast.error("Podaj poprawny numer telefonu."); return;
    }
    if (form.street.trim().length < 3) {
      toast.error("Podaj ulicę i numer domu/mieszkania."); return;
    }
    if (form.postalCode.trim().length < 5) {
      toast.error("Podaj poprawny kod pocztowy."); return;
    }
    if (form.city.trim().length < 2) {
      toast.error("Podaj miasto."); return;
    }
    const data = await checkout({ ...form, name: `${form.firstName.trim()} ${form.lastName.trim()}` });
    if (data && data.payment_method === "transfer") setOrderDone(data);
  };

  const copy = (text) => {
    navigator.clipboard?.writeText(text).then(() => toast.success("Skopiowano do schowka"));
  };

  if (orderDone) {
    return (
      <div className="mx-auto max-w-lg px-4 pb-24 pt-28" data-testid="checkout-transfer-confirmation">
        <div className="rounded-3xl glass-panel p-8">
          <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-400" />
            <h1 className="mt-3 font-display text-lg font-extrabold text-white">Zamówienie przyjęte!</h1>
            <p className="mt-1 text-sm text-slate-400">Numer zamówienia: <span className="font-mono font-bold text-cyan-300">{orderDone.order_id}</span></p>
          </div>
          <div className="mt-4 rounded-2xl border border-slate-800 bg-slate-900/40 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Dane do przelewu</div>
            <div className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-slate-900 px-4 py-3">
              <span className="font-mono text-sm font-bold text-cyan-200" data-testid="transfer-account">{orderDone.bank_account}</span>
              <button onClick={() => copy(orderDone.bank_account)} className="rounded-lg p-2 text-slate-400 hover:text-cyan-300" aria-label="Kopiuj numer konta" data-testid="transfer-copy-account"><Copy className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 space-y-1.5 text-sm text-slate-300">
              <div className="flex justify-between"><span className="text-slate-500">Odbiorca:</span><span className="font-semibold">{orderDone.company_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Kwota:</span><span className="font-bold text-white">{orderDone.total.toFixed(2)} zł</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Tytuł przelewu:</span><span className="font-mono font-semibold text-cyan-300">{orderDone.order_id}</span></div>
            </div>
          </div>
          <p className="mt-4 rounded-xl border border-cyan-500/25 bg-cyan-500/10 p-4 text-xs leading-relaxed text-cyan-200">
            Przy tej formie płatności produkt wysyłamy <b>natychmiast po otrzymaniu płatności</b> na nasze konto — na adres <b>{form.email}</b>. Potwierdzenie zamówienia właśnie do Ciebie poleciało mailem.
          </p>
          {orderDone.account_created ? (
            <div className="mt-4 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-4 text-xs leading-relaxed text-emerald-200" data-testid="account-created-info">
              <p className="font-bold text-emerald-100">Założyliśmy dla Ciebie konto — sprawdzisz w nim status zamówienia na stronie „Konto”.</p>
              <div className="mt-2 space-y-1">
                <div>Login: <span className="font-mono font-bold text-white">{orderDone.account_created.email}</span></div>
                <div className="flex items-center gap-2">Hasło: <span className="font-mono font-bold text-white" data-testid="account-created-password">{orderDone.account_created.password}</span>
                  <button onClick={() => copy(orderDone.account_created.password)} className="rounded-lg p-1 text-emerald-300 hover:text-white" aria-label="Kopiuj hasło"><Copy className="h-3.5 w-3.5" /></button>
                </div>
              </div>
              <p className="mt-2 text-emerald-300/80">Zalecamy zmianę hasła po pierwszym zalogowaniu.</p>
            </div>
          ) : (
            <p className="mt-4 rounded-xl border border-slate-700 bg-slate-900/60 p-4 text-xs leading-relaxed text-slate-400">
              Masz już konto na ten adres e-mail — zaloguj się swoim dotychczasowym hasłem, żeby zobaczyć status zamówienia.
            </p>
          )}
          <Link to="/" data-testid="transfer-close" className="mt-5 block w-full rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3.5 text-center font-display text-sm font-bold text-slate-950 transition-transform hover:scale-[1.02] active:scale-95">
            Wróć do sklepu
          </Link>
        </div>
      </div>
    );
  }

  if (!items.length) return null;

  return (
    <div className="mx-auto max-w-5xl px-4 pb-24 pt-28" data-testid="checkout-page">
      <Link to="/" className="mb-6 inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-cyan-300">
        <ArrowLeft className="h-4 w-4" /> Wróć do sklepu
      </Link>
      <h1 className="font-display text-2xl font-extrabold text-white sm:text-3xl">Dokończ zamówienie</h1>

      <form onSubmit={submit} className="mt-8 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-5">
          <section className="rounded-3xl glass-panel p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <User className="h-5 w-5 text-cyan-400" />
              <h2 className="font-display text-base font-bold text-white">Dane kontaktowe</h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <input data-testid="checkout-firstname" value={form.firstName} onChange={set("firstName")} placeholder="Imię" className={FIELD_CLASS} />
              <input data-testid="checkout-lastname" value={form.lastName} onChange={set("lastName")} placeholder="Nazwisko" className={FIELD_CLASS} />
              <input data-testid="checkout-email" type="email" value={form.email} onChange={set("email")} placeholder="Email" className={FIELD_CLASS} />
              <input data-testid="checkout-phone" type="tel" value={form.phone} onChange={set("phone")} placeholder="Numer telefonu" className={FIELD_CLASS} />
            </div>
          </section>

          <section className="rounded-3xl glass-panel p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <MapPin className="h-5 w-5 text-cyan-400" />
              <h2 className="font-display text-base font-bold text-white">Adres do faktury</h2>
            </div>
            <div className="grid gap-3">
              <input data-testid="checkout-street" value={form.street} onChange={set("street")} placeholder="Ulica i numer domu / mieszkania" className={FIELD_CLASS} />
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="checkout-postal" value={form.postalCode} onChange={set("postalCode")} placeholder="Kod pocztowy" className={FIELD_CLASS} />
                <input data-testid="checkout-city" value={form.city} onChange={set("city")} placeholder="Miasto" className={FIELD_CLASS} />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <input data-testid="checkout-company" value={form.companyName} onChange={set("companyName")} placeholder="Nazwa firmy (opcjonalnie)" className={FIELD_CLASS} />
                <input data-testid="checkout-nip" value={form.nip} onChange={set("nip")} placeholder="NIP (opcjonalnie)" className={FIELD_CLASS} />
              </div>
            </div>
          </section>

          <section className="rounded-3xl glass-panel p-6">
            <div className="mb-4 flex items-center gap-2.5">
              <CreditCard className="h-5 w-5 text-cyan-400" />
              <h2 className="font-display text-base font-bold text-white">Płatność</h2>
            </div>
            <div className="grid grid-cols-2 gap-2.5" data-testid="payment-methods">
              <button
                type="button"
                data-testid="pay-method-stripe"
                onClick={() => setForm((f) => ({ ...f, method: "stripe" }))}
                className={`rounded-xl border p-3 text-left transition-all ${form.method === "stripe" ? "border-cyan-400/70 bg-cyan-500/10 shadow-[0_8px_24px_-8px_rgba(6,182,212,.5)]" : "border-slate-700 bg-slate-900/60 hover:border-slate-600"}`}
              >
                <CreditCard className={`h-5 w-5 ${form.method === "stripe" ? "text-cyan-300" : "text-slate-400"}`} />
                <div className="mt-1.5 text-xs font-bold text-slate-100">Karta · BLIK · P24</div>
                <div className="text-[10px] text-slate-500">płatność obsługuje Stripe</div>
              </button>
              <button
                type="button"
                data-testid="pay-method-transfer"
                onClick={() => setForm((f) => ({ ...f, method: "transfer" }))}
                className={`rounded-xl border p-3 text-left transition-all ${form.method === "transfer" ? "border-teal-400/70 bg-teal-500/10 shadow-[0_8px_24px_-8px_rgba(45,212,191,.5)]" : "border-slate-700 bg-slate-900/60 hover:border-slate-600"}`}
              >
                <Building2 className={`h-5 w-5 ${form.method === "transfer" ? "text-teal-300" : "text-slate-400"}`} />
                <div className="mt-1.5 text-xs font-bold text-slate-100">Przelew na konto</div>
                <div className="text-[10px] text-slate-500">tradycyjny przelew bankowy</div>
              </button>
            </div>
            {form.method === "transfer" && bank && (
              <div className="mt-3 rounded-xl border border-teal-500/25 bg-teal-500/8 p-3.5 text-xs leading-relaxed text-slate-300" data-testid="transfer-info">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-500">Nr konta:</span>
                  <span className="font-mono font-bold text-teal-200">{bank.bank_account}</span>
                </div>
                <p className="mt-2 text-[11px] text-teal-200/90">
                  Przy tej formie płatności produkt wysyłamy <b>natychmiast po otrzymaniu płatności</b> na nasze konto.
                </p>
              </div>
            )}
          </section>
        </div>

        <aside className="h-fit space-y-4 rounded-3xl glass-panel p-6 lg:sticky lg:top-24">
          <div className="flex items-center gap-2.5">
            <ShoppingBag className="h-5 w-5 text-cyan-400" />
            <h2 className="font-display text-base font-bold text-white">Podsumowanie</h2>
          </div>
          <div className="space-y-3">
            {items.map((i) => {
              const Icon = FORMAT_ICONS[i.format] || BookOpen;
              return (
                <div key={i.id} className="flex items-center gap-3" data-testid={`checkout-item-${i.id}`}>
                  {i.cover_image ? (
                    <img src={i.cover_image} alt={i.title} className="h-14 w-10 shrink-0 rounded-lg object-cover shadow-lg" />
                  ) : (
                    <div className="flex h-14 w-10 shrink-0 items-center justify-center rounded-lg bg-cyan-500/15 text-cyan-400">
                      <Icon className="h-4 w-4" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-xs font-semibold text-slate-100">{i.title}</div>
                    <div className="text-[10px] uppercase tracking-wide text-slate-500">{i.format}</div>
                  </div>
                  <div className="text-xs font-bold text-cyan-300">{i.price.toFixed(2)} zł</div>
                  <button type="button" onClick={() => remove(i.id)} className="rounded-lg p-1 text-slate-500 transition-colors hover:text-rose-400" aria-label="Usuń">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between border-t border-slate-800 pt-4">
            <span className="text-sm text-slate-400">Razem</span>
            <span data-testid="cart-total" className="font-display text-xl font-extrabold text-white">{total.toFixed(2)} zł</span>
          </div>
          <button
            data-testid="cart-checkout-button"
            type="submit"
            disabled={!items.length || checkingOut}
            className="w-full rounded-xl bg-gradient-to-r from-cyan-500 to-teal-500 py-3.5 font-display text-sm font-bold text-slate-950 shadow-lg shadow-cyan-500/30 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-40"
          >
            {checkingOut ? "Przetwarzanie..." : form.method === "transfer" ? "Złóż zamówienie z przelewem" : "Przejdź do płatności"}
          </button>
          <p className="text-center text-[11px] text-slate-500">
            {form.method === "stripe"
              ? "Karty Visa / Mastercard, BLIK, Przelewy24 — bezpieczna płatność Stripe. Pliki otrzymasz natychmiast po opłaceniu."
              : "Po zaksięgowaniu przelewu wyślemy produkt e-mailem — natychmiast."}
          </p>
        </aside>
      </form>
    </div>
  );
}
